import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import SectionTitle from '../../components/common/SectionTitle';
import Card from '../../components/common/Card';
import ContactForm from '../../components/common/ContactForm';
import Messages from '../../components/common/Messages';
import { api } from '../../services/baseUrl';

const ClientSpace = () => {
  const { theme } = useContext(ThemeContext);
  const { user, refreshUser } = useContext(UserContext);
  const location = useLocation();
  const isDark = theme === 'dark';

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [emailNotice, setEmailNotice] = useState('');
  const [profileForm, setProfileForm] = useState({
    email: '',
    firstname: '',
    lastname: '',
    nickname: ''
  });

  // Données mockées pour l'affichage
  const mockOrders = [
    { id: 'ORD-2024-001', date: '15/12/2024', product: 'Bannière Twitch', status: 'En cours', price: '49€' },
    { id: 'ORD-2024-002', date: '10/12/2024', product: 'Logo Gaming Pro', status: 'Livré', price: '89€' },
    { id: 'ORD-2024-003', date: '05/12/2024', product: 'Pack Overlays', status: 'Livré', price: '149€' },
  ];

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [purchasedFiles, setPurchasedFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [unreadReplies, setUnreadReplies] = useState(0);

  const statusColors = {
    'En cours': isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700',
    'Livré': isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700',
    'En attente': isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
    'Terminé': isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700',
  };

  const tabs = [
    { key: 'dashboard', label: 'Tableau de bord', icon: '📊' },
    { key: 'orders', label: 'Mes commandes', icon: '🛍️' },
    { key: 'projects', label: 'Mes projets', icon: '📁' },
    ...(user?.id_role !== 3 ? [{ key: 'messages', label: 'Messages', icon: '💬' }] : []), // Masquer pour admin
    { key: 'files', label: 'Fichiers', icon: '📄' },
    { key: 'invoices', label: 'Factures', icon: '🧾' },
    { key: 'settings', label: 'Paramètres', icon: '⚙️' },
  ];

  // Activer l'onglet via ?tab=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const validTabs = user?.id_role === 3 
      ? ['dashboard', 'orders', 'projects', 'files', 'invoices', 'settings'] 
      : ['dashboard', 'orders', 'projects', 'messages', 'files', 'invoices', 'settings'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search, user]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        email: user.email || '',
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        nickname: user.nickname || ''
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (activeTab !== 'projects') return;
      try {
        setProjectsLoading(true);
        const response = await api.get('/projects/my');
        setProjects(response.data?.projects || []);
      } catch (error) {
        console.error('Erreur chargement projets:', error);
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };

    const fetchPurchasedFiles = async () => {
      if (activeTab !== 'files') return;
      try {
        setFilesLoading(true);
        const response = await api.get('/purchased-files');
        setPurchasedFiles(response.data?.files || []);
      } catch (error) {
        console.error('Erreur chargement fichiers:', error);
        setPurchasedFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };

    fetchProjects();
    fetchPurchasedFiles();
  }, [activeTab]);

  // Charger le nombre de messages non lus (conversations)
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (user?.id_role === 3) return; // Pas pour les admins
      try {
        const response = await api.get('/conversations/unread-count');
        if (response.data.success) {
          setUnreadReplies(response.data.unread_count || 0);
        }
      } catch (error) {
        console.error('Erreur comptage messages:', error);
      }
    };

    fetchUnreadCount();
    
    // Écouter les mises à jour de messages
    const handleMessagesUpdate = () => {
      fetchUnreadCount();
    };
    window.addEventListener('messagesUpdated', handleMessagesUpdate);
    
    // Recharger toutes les 30 secondes
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('messagesUpdated', handleMessagesUpdate);
    };
  }, [user, activeTab]);

  const handleProfileChange = (field) => (e) => {
    setProfileForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    const emailChanged = profileForm.email !== (user.email || '');
    try {
      setProfileSaving(true);
      const response = await api.put(`/users/${user.id_user || user.id}`, {
        email: profileForm.email,
        firstname: profileForm.firstname,
        lastname: profileForm.lastname,
        nickname: profileForm.nickname
      });

      if (emailChanged) {
        toast.success('Email modifié. Un email de vérification a été envoyé.');
        setEmailNotice('Email modifié : vérifiez votre boîte mail pour valider votre nouvelle adresse.');
      } else {
        toast.success('Informations mises à jour.');
        setEmailNotice('');
      }

      await refreshUser();

      // Compatibilité si l’API renvoie user directement
      if (response?.data?.user) {
        setProfileForm({
          email: response.data.user.email || '',
          firstname: response.data.user.firstname || '',
          lastname: response.data.user.lastname || '',
          nickname: response.data.user.nickname || ''
        });
      }
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <SectionTitle
          title="Espace client"
          subtitle="Gérez vos commandes, projets et support"
          align="left"
        />

        {/* Navigation par onglets */}
        <div className={`mb-8 overflow-x-auto ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'} rounded-lg`}>
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap select-none relative ${
                  activeTab === tab.key
                    ? isDark
                      ? 'bg-rose-500/20 text-rose-400 border-b-2 border-rose-500'
                      : 'bg-rose-50 text-rose-600 border-b-2 border-rose-600'
                    : isDark
                    ? 'text-white/70 hover:bg-white/5'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.key === 'messages' && unreadReplies > 0 && (
                  <span className="relative flex h-5 w-5 ml-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-5 w-5 bg-gradient-to-br from-rose-500 to-pink-600 items-center justify-center text-white text-xs font-bold shadow-lg">
                      {unreadReplies > 9 ? '9+' : unreadReplies}
                    </span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Contenu des onglets */}
        <div>
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <Card>
                <h3 className="text-xl font-semibold mb-3">Bienvenue</h3>
                <p className={isDark ? 'text-white/70' : 'text-gray-600'}>
                  Retrouvez ici vos commandes, projets et messages de support.
                </p>
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-3">Activité récente</h3>
                <div className="space-y-2">
                  <div className={`p-3 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <p className="font-medium">Nouveau fichier disponible</p>
                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Hier à 14:32</p>
                  </div>
                  <div className={`p-3 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <p className="font-medium">Facture #2024-002 générée</p>
                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>10/12/2024</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Commandes */}
          {activeTab === 'orders' && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Mes commandes</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <tr>
                      <th className="text-left p-3 font-semibold">Référence</th>
                      <th className="text-left p-3 font-semibold">Date</th>
                      <th className="text-left p-3 font-semibold">Produit</th>
                      <th className="text-left p-3 font-semibold">Statut</th>
                      <th className="text-left p-3 font-semibold">Prix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockOrders.map(order => (
                      <tr key={order.id} className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <td className="p-3 font-mono text-sm">{order.id}</td>
                        <td className="p-3">{order.date}</td>
                        <td className="p-3">{order.product}</td>
                        <td className="p-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status]}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="p-3 font-semibold">{order.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Projets */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              <Card>
                <p className={isDark ? 'text-white/70' : 'text-gray-600'}>
                  Cet onglet est en cours de développement.
                </p>
              </Card>
              <Card>
                <h3 className="text-xl font-semibold mb-4">Mes projets</h3>
                {projectsLoading ? (
                  <p className={isDark ? 'text-white/70' : 'text-gray-600'}>Chargement...</p>
                ) : projects.length === 0 ? (
                  <p className={isDark ? 'text-white/70' : 'text-gray-600'}>Aucun projet pour le moment.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <tr>
                          <th className="text-left p-3 font-semibold">Projet</th>
                          <th className="text-left p-3 font-semibold">Type</th>
                          <th className="text-left p-3 font-semibold">Statut</th>
                          <th className="text-left p-3 font-semibold">Progression</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map(project => (
                          <tr key={project.id_project} className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                            <td className="p-3">
                              <div className="font-medium">{project.name}</div>
                              {project.description && (
                                <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{project.description}</div>
                              )}
                            </td>
                            <td className="p-3">{project.type || '-'}</td>
                            <td className="p-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[project.status] || (isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700')}`}>
                                {project.status}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-32 h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                  <div
                                    className={`h-full rounded-full ${isDark ? 'bg-rose-500' : 'bg-rose-600'}`}
                                    style={{ width: `${project.progress || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-semibold">{project.progress || 0}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Messages (Support + historique) */}
          {activeTab === 'messages' && (
            <div className="space-y-6">
              <Card>
                <h3 className="text-xl font-semibold mb-4">Vos messages</h3>
                <Messages />
              </Card>
            </div>
          )}

          {/* Fichiers */}
          {activeTab === 'files' && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Mes fichiers téléchargeables</h3>
              
              {filesLoading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : purchasedFiles.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  <p className="mb-2">Aucun fichier disponible pour le moment.</p>
                  <p className="text-sm">Les fichiers de vos achats apparaîtront ici après paiement.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {purchasedFiles.map((file, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">{file.product_name || 'Fichier'}</h4>
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                            Commande: {file.order_number} - {new Date(file.purchased_at).toLocaleDateString('fr-FR')}
                          </p>
                          {file.file_name && (
                            <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                              📄 {file.file_name}
                            </p>
                          )}
                        </div>
                        <a
                          href={`${api.defaults.baseURL}/purchased-files/${file.id}/download`}
                          download
                          className={`px-4 py-2 rounded font-medium transition ${isDark ? 'bg-rose-500 hover:bg-rose-600' : 'bg-rose-600 hover:bg-rose-700'} text-white`}
                        >
                          ⬇️ Télécharger
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}



          {/* Factures */}
          {activeTab === 'invoices' && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Mes factures</h3>
              <p className={isDark ? 'text-white/70' : 'text-gray-600'}>
                Vos factures seront disponibles ici après vos achats.
              </p>
            </Card>
          )}

          {/* Paramètres */}
          {activeTab === 'settings' && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Paramètres du compte</h3>
              <div className="space-y-4">
                {!user?.email_verified_at && (
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-500/10 border border-amber-500/30 text-amber-200' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
                    Votre email n’est pas vérifié. {emailNotice || 'Un email de vérification vous sera envoyé si vous changez votre adresse.'}
                  </div>
                )}
                {user?.email_verified_at && emailNotice && (
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-500/10 border border-blue-500/30 text-blue-200' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
                    {emailNotice}
                  </div>
                )}
                <div>
                  <label className="block font-medium mb-2">Email</label>
                  <input 
                    type="email" 
                    value={profileForm.email}
                    onChange={handleProfileChange('email')}
                    className={`w-full px-4 py-2 rounded border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    placeholder="votre@email.com"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2">Prénom</label>
                    <input 
                      type="text" 
                      value={profileForm.firstname}
                      onChange={handleProfileChange('firstname')}
                      className={`w-full px-4 py-2 rounded border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Nom</label>
                    <input 
                      type="text" 
                      value={profileForm.lastname}
                      onChange={handleProfileChange('lastname')}
                      className={`w-full px-4 py-2 rounded border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      placeholder="Votre nom"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-medium mb-2">Pseudo</label>
                  <input 
                    type="text" 
                    value={profileForm.nickname}
                    onChange={handleProfileChange('nickname')}
                    className={`w-full px-4 py-2 rounded border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    placeholder="Votre pseudo"
                  />
                </div>
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className={`px-6 py-2 rounded font-medium ${isDark ? 'bg-rose-500 hover:bg-rose-600' : 'bg-rose-600 hover:bg-rose-700'} text-white ${profileSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {profileSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </Card>
          )}
        </div>

        {/* Note d'information */}
        <div className={`mt-8 p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
          <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
            <strong>Note :</strong> Cet espace client est actuellement en préparation. Les fonctionnalités complètes seront disponibles prochainement.
          </p>
        </div>

      </div>
    </div>
  );
};

export default ClientSpace;
